import type {
  PropertyNeedsResponseDto,
  ProspectedPropertyResponseDto,
  NumericRangeResponseDto,
} from "@shared/generated/crm/models";

export const INTENT_LABELS: Record<string, string> = {
  BUY: "Buyer",
  SELL: "Seller",
  RENT: "Renter",
  RENT_OUT: "Landlord",
};

function formatRange(
  range: NumericRangeResponseDto,
  fmt: (n: number) => string,
): string | null {
  const { min, max } = range;
  if (min != null && max != null) return `${fmt(min)}–${fmt(max)}`;
  if (min != null) return `${fmt(min)}+`;
  if (max != null) return `up to ${fmt(max)}`;
  return null;
}

export function formatBudget(range: NumericRangeResponseDto): string | null {
  return formatRange(range, (n) => `$${n.toLocaleString()}`);
}

function formatCount(range: NumericRangeResponseDto): string | null {
  return formatRange(range, String);
}

export function buildPreferenceLines(
  needs: PropertyNeedsResponseDto,
): string[] {
  const lines: string[] = [];
  const parts: string[] = [];
  if (needs.bedrooms) {
    const bd = formatCount(needs.bedrooms);
    if (bd) parts.push(`${bd} bd`);
  }
  if (needs.bathrooms) {
    const ba = formatCount(needs.bathrooms);
    if (ba) parts.push(`${ba} ba`);
  }
  if (needs.propertyType) parts.push(needs.propertyType);
  if (parts.length) lines.push(parts.join(" / "));
  if (needs.squareFootage) {
    const sqft = formatRange(
      needs.squareFootage,
      (n) => `${n.toLocaleString()} sqft`,
    );
    if (sqft) lines.push(sqft);
  }
  if (needs.location) lines.push(needs.location);
  needs.preferences?.forEach((p) => lines.push(p));
  return lines;
}

export function buildPropertyLines(
  property: ProspectedPropertyResponseDto,
): string[] {
  const lines: string[] = [];
  const address = [property.addressLine1, property.city, property.state]
    .filter(Boolean)
    .join(", ");
  if (address) lines.push(address);
  if (property.estimatedPrice != null)
    lines.push(`Est. $${property.estimatedPrice.toLocaleString()}`);
  if (property.bedCount != null || property.bathCount != null) {
    const parts = [];
    if (property.bedCount != null) parts.push(`${property.bedCount} bd`);
    if (property.bathCount != null) parts.push(`${property.bathCount} ba`);
    lines.push(parts.join(" / "));
  }
  return lines;
}
